import { useState } from "react"
import { DashboardLayout } from "@/components/dashboard/dashboard-layout"
import { TabGroup, TabGroupList, TabGroupTrigger } from "@/components/ui/tab-group"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { User, Phone, Mail, Lock } from "lucide-react"
import Image from "@/src/components/Image"

export default function AccountsSettingsPage() {
  const [activeTab, setActiveTab] = useState<"profile" | "password">("profile")
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
  })
  const [passwordData, setPasswordData] = useState({
    phoneOrOtp: "",
    otp: "",
    newPassword: "",
    confirmPassword: "",
  })

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handlePasswordInputChange = (field: string, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSave = () => {
    console.log("Saving account data:", formData)
  }

  const handleSendOTP = () => {
    console.log("Sending OTP to:", passwordData.phoneOrOtp)
  }

  const handlePasswordChange = () => {
    console.log("Changing password:", passwordData)
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Page Title */}
        <h1 className="text-2xl font-bold text-gray-900">ตั้งค่าบัญชี</h1>

        {/* Tabs */}
        <TabGroup 
          defaultValue="profile" 
          color="brand-blue" 
          fullWidth
          onValueChange={(value) => setActiveTab(value as "profile" | "password")}
        >
          <TabGroupList>
            <TabGroupTrigger value="profile">ข้อมูลส่วนตัว</TabGroupTrigger>
            <TabGroupTrigger value="password">เปลี่ยนรหัสผ่าน</TabGroupTrigger>
          </TabGroupList>
        </TabGroup>

        {/* Form Card */}
        {activeTab === "profile" && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* First Name */}
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-gray-900 font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  ชื่อ
                </Label>
                <Input
                  id="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange("firstName", e.target.value)}
                  placeholder="กรอกชื่อ"
                  className="bg-white border-gray-300"
                />
              </div>

              {/* Last Name */}
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-gray-900 font-medium flex items-center gap-2">
                  <User className="w-4 h-4" />
                  นามสกุล
                </Label>
                <Input
                  id="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange("lastName", e.target.value)}
                  placeholder="กรอกนามสกุล"
                  className="bg-white border-gray-300"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-gray-900 font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  เบอร์โทรศัพท์
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  placeholder="กรอกเบอร์โทรศัพท์"
                  className="bg-white border-gray-300"
                />
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-900 font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  อีเมล
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  placeholder="กรอกอีเมล"
                  className="bg-white border-gray-300"
                />
                <p className="text-sm text-red-500 mt-1">
                  หากต้องการรับใบเสร็จรับเงินเข้าเติมเงิน หรือ ซื้อแพ็กเกจ กรุณากรอก อีเมล ให้ถูกต้อง
                </p>
              </div>
            </div>

            {/* Login Methods Section */}
            <div className="space-y-4 mb-8">
              <h2 className="text-lg font-medium text-gray-900">ช่องทางการเข้าสู่ระบบ</h2>
              <div className="flex items-center gap-3">
                <button className="w-12 h-12 rounded-full overflow-hidden hover:opacity-80 transition-opacity">
                  <Image
                    src="/images/design-mode/icn01.png"
                    alt="LINE"
                    width={48}
                    height={48}
                    className="w-full h-full object-cover"
                  />
                </button>
                <button className="w-12 h-12 rounded-full overflow-hidden hover:opacity-80 transition-opacity">
                  <Image
                    src="/images/design-mode/icn02.png"
                    alt="Facebook"
                    width={48}
                    height={48}
                    className="w-full h-full object-cover"
                  />
                </button>
                <button className="w-12 h-12 rounded-full overflow-hidden hover:opacity-80 transition-opacity">
                  <Image
                    src="/images/design-mode/icn03.png"
                    alt="Phone"
                    width={48}
                    height={48}
                    className="w-full h-full object-cover"
                  />
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                className="bg-[#f87171] hover:bg-[#ef4444] text-white px-8 py-2 rounded-lg font-medium"
              >
                บันทึก
              </Button>
            </div>
          </div>
        )}

        {/* Password Change Tab */}
        {activeTab === "password" && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              {/* Phone Number */}
              <div className="space-y-2">
                <Label htmlFor="phoneOrOtp" className="text-gray-900 font-medium flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  เบอร์โทรศัพท์
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="phoneOrOtp"
                    type="tel"
                    value={passwordData.phoneOrOtp}
                    onChange={(e) => handlePasswordInputChange("phoneOrOtp", e.target.value)}
                    placeholder="กรอกเบอร์โทรศัพท์"
                    className="bg-white border-gray-300 flex-1"
                  />
                  <Button
                    onClick={handleSendOTP}
                    className="bg-[#60a5fa] hover:bg-[#3b82f6] text-white px-6 whitespace-nowrap"
                  >
                    ขอรหัส OTP
                  </Button>
                </div>
              </div>

              {/* OTP */}
              <div className="space-y-2">
                <Label htmlFor="otp" className="text-gray-900 font-medium flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  OTP
                </Label>
                <Input
                  id="otp"
                  type="text"
                  value={passwordData.otp}
                  onChange={(e) => handlePasswordInputChange("otp", e.target.value)}
                  placeholder="กรอกรหัส OTP"
                  className="bg-white border-gray-300"
                />
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-gray-900 font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  รหัสผ่านใหม่
                </Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordInputChange("newPassword", e.target.value)}
                  placeholder="กรอกเบอร์โทรศัพท์"
                  className="bg-white border-gray-300"
                />
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-gray-900 font-medium flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  ยืนยันรหัสผ่าน
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordInputChange("confirmPassword", e.target.value)}
                  placeholder="กรอกอีเมล"
                  className="bg-white border-gray-300"
                />
                <p className="text-sm text-red-500 mt-1">
                  รหัสผ่านจะต้องมีขั้ามาน 8-16 ตัวอักษร ใช้อักษรตัวเล็ก 1, ตัวใหญ่ 1, ตัวเลข 1
                </p>
              </div>
            </div>

            {/* Login Methods Section */}
            <div className="space-y-4 mb-8">
              <h2 className="text-lg font-medium text-gray-900">ช่องทางการเข้าสู่ระบบ</h2>
              <div className="flex items-center gap-3">
                <button className="w-12 h-12 rounded-full overflow-hidden hover:opacity-80 transition-opacity">
                  <Image
                    src="/images/design-mode/icn01.png"
                    alt="LINE"
                    width={48}
                    height={48}
                    className="w-full h-full object-cover"
                  />
                </button>
                <button className="w-12 h-12 rounded-full overflow-hidden hover:opacity-80 transition-opacity">
                  <Image
                    src="/images/design-mode/icn02.png"
                    alt="Facebook"
                    width={48}
                    height={48}
                    className="w-full h-full object-cover"
                  />
                </button>
                <button className="w-12 h-12 rounded-full overflow-hidden hover:opacity-80 transition-opacity">
                  <Image
                    src="/images/design-mode/icn03.png"
                    alt="Phone"
                    width={48}
                    height={48}
                    className="w-full h-full object-cover"
                  />
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handlePasswordChange}
                className="bg-[#f87171] hover:bg-[#ef4444] text-white px-8 py-2 rounded-lg font-medium"
              >
                ยืนยัน
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
